import View                      from '@widesk-ui/components/View';
import Image                     from '@widesk-ui/components/Image';
import currentEditorStore        from '@/TokamapEditor/stores/currentEditorStore';
import themeStore                from '@/TokamapEditor/stores/themeStore';
import { BorderOutlined }        from '@ant-design/icons';
import { GatewayOutlined }       from '@ant-design/icons';
import { LineOutlined }          from '@ant-design/icons';
import CircleOutlined            from '@/TokamapEditor/ux/icons/CircleOutlined';
import { DEFAULT_TEXT_COLOR }    from '@/TokamapEditor/ux/PreviewLetter';
import PreviewLetter             from '@/TokamapEditor/ux/PreviewLetter';
import { previewBorderWidth }    from '@/TokamapEditor/ux/shapeStylePreview';
import React                     from 'react';
import useDesignTokens           from '@/hooks/useDesignTokens';
import AreaImageSection          from '@/TokamapEditor/ux/AreaSidebar/AreaImageSection';
import SectionTiles              from '@/TokamapEditor/ux/AreaSidebar/SectionTiles';
import shapeMeasure              from '@/TokamapEditor/helpers/shapeMeasure';
import { SHOW_SHAPE_MEASURES }   from '@/TokamapEditor/ux/constants';
import { FontColorsOutlined }    from '@ant-design/icons';
import { FormatPainterOutlined } from '@ant-design/icons';
import { PictureOutlined }       from '@ant-design/icons';
import { SelectOutlined }        from '@ant-design/icons';
import AreaThemeSection          from '@/TokamapEditor/ux/AreaSidebar/AreaThemeSection';
import AreaActionSection         from '@/TokamapEditor/ux/AreaSidebar/AreaActionSection';
import { observer }              from 'mobx-react-lite';
import AreaTextSection           from './AreaTextSection';
import { LeftSidebarHeader }     from '@/TokamapEditor/ux/LeftSidebar';
import leftSidebarStore          from '@/TokamapEditor/stores/leftSidebarStore';
import Alert                     from '@widesk-ui/components/Alert';

type Section = 'theme' | 'icon' | 'text' | 'action';

/**
 * Les MÊMES icônes que le menu des outils de dessin (`ShapesButton`) : l'utilisateur les associe déjà à
 * chaque type de forme, autant les réutiliser plutôt que d'en inventer d'autres.
 */
const SHAPE_KIND_ICONS: Record<string, React.ReactNode> = {
	polygon: <GatewayOutlined />,
	rectangle: <BorderOutlined />,
	circle: <CircleOutlined />,
	line: <LineOutlined />,
	image: <PictureOutlined />,
};

const SHAPE_KIND_LABELS: Record<string, string> = {
	polygon: 'Polygone',
	rectangle: 'Rectangle',
	circle: 'Cercle',
	line: 'Ligne',
	image: 'Image',
};

const FEMININE_KINDS = ['line', 'image'];

type AreaSidebarProps = {
	embedded?: boolean;
	top?: React.ReactNode;
	settingsHidden?: boolean;
};

const AreaSidebar = observer(({ embedded, top, settingsHidden }: AreaSidebarProps) => {
	const [section, setSection] = React.useState<Section>('theme');
	const tokens = useDesignTokens();

	const editor = currentEditorStore.get;
	const shapes = currentEditorStore.sidebarShapes;
	const shape = shapes.length === 1 ? shapes[0] : undefined;
	const isMulti = currentEditorStore.isMultiSelection;
	const fresh = !!shape && currentEditorStore.freshShapeId === shape.id;
	const kindLabel = SHAPE_KIND_LABELS[shape?.shapeKind ?? 'polygon'] ?? SHAPE_KIND_LABELS.polygon;
	const isLine = shapes.some(s => s.shapeKind === 'line');
	// Une forme-image n'a ni thème (fond/bordure toujours transparents, voir
	// `MapLibreAdapter.resolveShapeVisualProps`), ni texte, ni action pertinents — seul l'onglet
	// Image reste affiché pour elle (voir `AreaImageSection`, qui masque en plus ses propres champs
	// de positionnement pour ce type de forme).
	const isImage = shapes.every(s => s.shapeKind === 'image');

	const sectionOptions = [
		...(isImage ? [] : [{ value: 'theme' as const, label: 'Apparence', icon: <FormatPainterOutlined /> }]),
		...(isLine ? [] : [
			{ value: 'icon' as const, label: 'Image', icon: <PictureOutlined /> },
			...(isImage ? [] : [{ value: 'text' as const, label: 'Texte', icon: <FontColorsOutlined /> }]),
		]),
		...(isImage ? [] : [{ value: 'action' as const, label: 'Action', icon: <SelectOutlined /> }]),
	];

	React.useEffect(() => {
		if (!editor) return;

		return editor.mapAdapter.on(event => {
			if (event.type !== 'drawing:started') return;
			leftSidebarStore.close();
		});
	}, [editor]);

	React.useEffect(() => {
		if (isLine && (section === 'icon' || section === 'text')) setSection('theme');
		if (isImage && section !== 'icon') setSection('icon');
	}, [isLine, isImage, section]);

	return (
		<View heightF style={{ minHeight: 0 }}>
			{!embedded && (
				<LeftSidebarHeader
					title={
						isMulti
							? `${shapes.length} formes sélectionnées`
							: shape
								// Juste après le tracé, l'identifiant ne dit rien à personne : on dit d'où vient la
								// forme, comme dans la maquette.
								? fresh
									? `${kindLabel} tracé${FEMININE_KINDS.includes(shape.shapeKind ?? '') ? 'e' : ''} à l'instant`
									: `${kindLabel} #${currentEditorStore.getFeatureIdFromShapeId(shape.id)}`
								: ''
					}
					parentTitle={fresh ? 'Nouvelle forme' : undefined}
					renderTopLeft={isMulti || fresh ? undefined : () => <ShapeSwatch />}
					subTitle={isMulti || fresh ? undefined : <ShapeMetaTags />}
					closeButton
					largeTitle
				/>
			)}

			{top}

			{!settingsHidden && (
				<View paddingH="lg" style={{ paddingTop: 12, paddingBottom: 10, borderBottom: `1px solid ${tokens.border.subtle}` }}>
					<SectionTiles value={section} onChange={setSection} options={sectionOptions} />
				</View>
			)}

			<View flex paddingH="lg" paddingV="md" gap="md" style={{ minHeight: 0, overflowY: 'auto', display: settingsHidden ? 'none' : undefined }}>
				{isMulti && (
					<Alert
						type="warning"
						showIcon
						message={`Attention : toute modification s'appliquera aux ${shapes.length} formes sélectionnées.`}
					/>
				)}

				<div key={section} className="section-tiles-panel">
					{(() => {
						switch (section) {
							case 'theme':
								return <AreaThemeSection />;
							case 'text':
								return isLine ? null : <AreaTextSection />;
							case 'icon':
								return isLine ? null : <AreaImageSection />;
							case 'action':
								return <AreaActionSection />;
							default:
								return null;
						}
					})()}
				</div>
			</View>
		</View>
	);
});

export default AreaSidebar;

/** Côté du carré d'aperçu — la même valeur sert de base au plafond d'épaisseur de sa bordure. */
const SHAPE_SWATCH_SIZE = 46;

const ShapeSwatch = observer(() => {
	const tokens = useDesignTokens();
	const shape = currentEditorStore.selectedShape;

	if (!shape) return null;

	// Une forme liée à un thème rend l'apparence du THÈME, pas la sienne (voir `applyShapeTheme` dans
	// `@tokamap/core`) : ce carré doit montrer ce que l'utilisateur voit sur la carte. Remplacement et
	// non fusion, même règle que le rendu — d'où `theme.style` pris en bloc.
	//
	// `themeId` renseigné mais absent de la table (thème supprimé) : on retombe sur le style propre de
	// la forme, exactement comme le rendu.
	const theme = shape.themeId ? themeStore.coreThemes[shape.themeId] : undefined;
	const style = theme ? theme.style : shape.style;
	const text = theme ? theme.text : shape.text;

	// Lettre-spécimen : l'initiale du texte de la forme quand elle en a un, sinon un `A` neutre. Elle
	// ne prétend pas montrer un contenu — elle sert à donner à voir la couleur du texte et son halo,
	// comme dans `ThemeSwatch` (un thème n'a jamais de contenu non plus).
	const letter = text?.color || text?.bgColor || shape.text?.text
		? (shape.text?.text?.trim().charAt(0).toUpperCase() || 'A')
		: undefined;

	// Une forme-image (`shapeKind: "image"`) ne rend NI fond NI bordure sur la carte : l'image est
	// toute sa représentation (voir `resolveShapeVisualProps`). Une forme ORDINAIRE qui porte une
	// image, elle, garde les siens — ils restent visibles dans les marges laissées par le mode
	// "contain". D'où un seul rendu ci-dessous plutôt qu'une branche séparée pour l'image, qui
	// escamotait fond et bordure dans les deux cas.
	const isImageShape = shape.shapeKind === 'image';

	return (
		<View
			w={SHAPE_SWATCH_SIZE}
			h={SHAPE_SWATCH_SIZE}
			br={8}
			center
			style={{
				flexShrink: 0,
				overflow: 'hidden',
				// `border-box` : indispensable dès que l'épaisseur varie, sinon la bordure s'ajoute autour
				// des 46px et le carré change de taille d'une forme à l'autre (voir `previewBorderWidth`).
				boxSizing: 'border-box',
				background: isImageShape ? undefined : (style?.bgColor || tokens.surface.subtle),
				border: isImageShape
					// Bordure neutre : sans elle, une forme-image dont l'image ne remplit pas le carré
					// n'aurait aucun contour et flotterait dans le vide.
					? `1px solid ${tokens.border.default}`
					: [
						`${previewBorderWidth(style?.borderWidth, SHAPE_SWATCH_SIZE)}px`,
						style?.borderDashed ? 'dashed' : 'solid',
						style?.borderColor || tokens.color.primary,
					].join(' '),
			}}
		>
			{/* `contain` comme sur la carte : l'image ne recouvre pas le fond, elle se pose dessus en
			    laissant voir ses marges. */}
			{!!shape.image?.src && (
				<Image
					// Adresse brute, sans CORS : voir `ImageThumbnail`.
					src={shape.image.src}
					preview={false}
					style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
				/>
			)}

			{!shape.image?.src && (letter ? (
				<PreviewLetter letter={letter} text={text} size={SHAPE_SWATCH_SIZE} />
			) : (
				// Ni image ni texte : sans repère, le carré n'était qu'un aplat de couleur qui donnait
				// l'impression d'un aperçu incomplet. L'icône du TYPE de forme le remplit avec une
				// information utile.
				//
				// Couleur du TEXTE, comme la lettre-spécimen qu'elle remplace. Elle vaut ici toujours le
				// noir par défaut de l'adapter : l'icône ne s'affiche justement que quand aucune couleur de
				// texte n'est définie. Conséquence à connaître — sur un fond très sombre, elle se voit peu.
				<View
					size={Math.round(SHAPE_SWATCH_SIZE * 0.45)}
					color={text?.color || DEFAULT_TEXT_COLOR}
					style={{ lineHeight: 1, opacity: 0.85 }}
				>
					{SHAPE_KIND_ICONS[shape.shapeKind ?? 'polygon'] ?? SHAPE_KIND_ICONS.polygon}
				</View>
			))}
		</View>
	);
});

const ShapeMetaTags = observer(() => {
	const tokens = useDesignTokens();
	const shape = currentEditorStore.selectedShape;

	if (!shape) return null;

	const poiId = (shape.entity as any)?.entity?.id;
	const measure = SHOW_SHAPE_MEASURES ? shapeMeasure(shape) : undefined;

	return (
		<View row gap={6} wrap>
			<Tag>{`Calque ${shape.layer ?? '—'}`}</Tag>
			{!!poiId && <Tag>{`POI ${poiId}`}</Tag>}
			{!!measure && <Tag>{measure}</Tag>}
		</View>
	);

	function Tag({ children }: { children: React.ReactNode; }) {
		return (
			<View
				size={11}
				px={7}
				py={2}
				br={100}
				color={tokens.text.muted}
				style={{ background: tokens.surface.subtle }}
			>
				{children}
			</View>
		);
	}
});
